import type { GuidePlacement } from '../engine/types';
import type { GuideTopicId } from './topics';

/**
 * Narrower than GuideTour on purpose: naming a topic that does not exist is a
 * compile error here, not a step silently skipped at runtime.
 */
type TypedTour = {
  steps: readonly { topicId: GuideTopicId; placement: GuidePlacement }[];
};

export const GUIDE_TOURS = {
  'wizard.basic-info': {
    steps: [
      {
        topicId: 'wizard.basicInfo.name',
        placement: 'bottom',
      },
      {
        topicId: 'wizard.basicInfo.description',
        placement: 'top',
      },
      {
        topicId: 'wizard.basicInfo.prizes',
        placement: 'top',
      },
      {
        topicId: 'wizard.basicInfo.banner',
        placement: 'top',
      },
    ],
  },
  // americanFinalBracket is absent on purpose: AmericanFinalBracketSizeField is
  // rendered by CreateFixtureModal after the tournament exists, never by this
  // step, so a tour stop pointing at it would always be skipped.
  'wizard.fixture-config': {
    steps: [
      {
        topicId: 'wizard.fixture.fixtureType',
        placement: 'bottom',
      },
      {
        topicId: 'wizard.fixture.format',
        placement: 'top',
      },
      {
        topicId: 'wizard.fixture.groupsFixed',
        placement: 'top',
      },
      {
        topicId: 'wizard.fixture.overflowGroupClassification',
        placement: 'top',
      },
      {
        topicId: 'wizard.fixture.americanGroupFormat',
        placement: 'top',
      },
      {
        topicId: 'wizard.fixture.teamsAllowed',
        placement: 'top',
      },
    ],
  },
  // forceSum has no stop of its own: CategoryRangeField swaps its title between
  // range and sum on the same card, so both modes share one anchor. The inline
  // FieldHelp there follows the active mode.
  'wizard.team-config': {
    steps: [
      {
        topicId: 'wizard.team.gender',
        placement: 'bottom',
      },
      {
        topicId: 'wizard.team.playersPerTeam',
        placement: 'top',
      },
      {
        topicId: 'wizard.team.categories',
        placement: 'top',
      },
      {
        topicId: 'wizard.team.ageRestriction',
        placement: 'top',
      },
    ],
  },
  'wizard.pricing': {
    steps: [
      {
        topicId: 'wizard.pricing.paymentType',
        placement: 'right',
      },
      {
        topicId: 'wizard.pricing.price',
        placement: 'right',
      },
      {
        topicId: 'wizard.pricing.fee',
        placement: 'left',
      },
    ],
  },
  'wizard.tournament-config': {
    steps: [
      {
        topicId: 'wizard.config.signupDeadline',
        placement: 'bottom',
      },
      {
        topicId: 'wizard.config.matchDuration',
        placement: 'top',
      },
      {
        topicId: 'wizard.config.minSchedulesRequired',
        placement: 'top',
      },
      {
        topicId: 'wizard.config.countsForCategoryRanking',
        placement: 'top',
      },
      {
        topicId: 'wizard.config.categoryRankingTarget',
        placement: 'top',
      },
    ],
  },
  'wizard.club-schedules': {
    steps: [
      {
        topicId: 'wizard.schedules.clubs',
        placement: 'bottom',
      },
      {
        topicId: 'wizard.schedules.blocks',
        placement: 'bottom',
      },
      {
        topicId: 'wizard.schedules.dates',
        placement: 'bottom',
      },
    ],
  },
  // No capacity stop: the capacity preview the feature doc describes is not in
  // the code (no calculate/capacity call anywhere), so there is nothing to
  // point at.
  'wizard.summary': {
    steps: [
      {
        topicId: 'wizard.summary.review',
        placement: 'bottom',
      },
      {
        topicId: 'wizard.summary.editable',
        placement: 'top',
      },
    ],
  },
  'panel.clubs': {
    steps: [
      {
        topicId: 'panel.clubs.list',
        placement: 'top',
      },
    ],
  },
  'panel.tournaments': {
    steps: [
      {
        topicId: 'panel.tournaments.list',
        placement: 'top',
      },
      {
        topicId: 'panel.tournaments.create',
        placement: 'bottom',
      },
    ],
  },
  'panel.current-account': {
    steps: [
      {
        topicId: 'panel.currentAccount.balance',
        placement: 'bottom',
      },
      {
        topicId: 'panel.currentAccount.movements',
        placement: 'top',
      },
    ],
  },
  'panel.profile': {
    steps: [
      {
        topicId: 'panel.profile.data',
        placement: 'bottom',
      },
      {
        topicId: 'panel.profile.guides',
        placement: 'top',
      },
    ],
  },
  'panel.inicio': {
    steps: [
      {
        topicId: 'panel.inicio.tournaments',
        placement: 'bottom',
      },
      {
        topicId: 'panel.inicio.mercadopago',
        placement: 'top',
      },
    ],
  },
} as const satisfies Record<string, TypedTour>;

/** Every tour id that exists. */
export type GuideTourId = keyof typeof GUIDE_TOURS;
